import React, { useState } from 'react';
import { useProfile } from '../../contexts/ProfileContext';
import { authFetch } from '../../utils/auth';
import { useNavigate } from 'react-router-dom';
import Wordmark from '../../components/common/Wordmark';

export default function WizardLayout({ step, total, children, onNext, nextDisabled }) {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { profile, refresh } = useProfile();
  
  
  const handleNext = async () => {
    setLoading(true);
    try {
      await onNext();
    } finally {
      setLoading(false);
    }
  };
  
  const handleSkip = async () => {
    try {
      const skipData = {
        ...profile,
        onboardingSkipped: true,
        profileComplete: true,
        lastOnboardingStep: step // Track where user left off
      };
      
      // Mark onboarding as skipped in profile and save current step
      const response = await authFetch('/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(skipData)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Backend save failed:', errorText);
      }
      
      // Refresh profile context and wait for it to complete
      await refresh();
      
      // Give the context time to update
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Show loading overlay for smooth transition
      const loadingElement = document.createElement('div');
      loadingElement.className = 'fixed inset-0 bg-white bg-opacity-90 flex items-center justify-center z-50 transition-opacity duration-300';
      loadingElement.innerHTML = `
        <div class="text-center">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p class="text-gray-600">Tar deg til dashbord...</p>
        </div>
      `;
      document.body.appendChild(loadingElement);
      
      // Small delay for smooth transition
      setTimeout(() => {
        window.location.href = '/home';
      }, 300);
    } catch (error) {
      console.error('Error skipping onboarding:', error);
      // Still navigate even if API call fails
      navigate('/home', { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-4">
        {/* Logo header */}
        <div className="pt-8 pb-4">
          <Wordmark size="lg" />
        </div>

        <div className="py-8">
          {/* Progress bar with step indicators */}
          <div className="mb-12">
            <div className="flex justify-between items-center mb-4">
              <span className="text-sm font-medium text-text-secondary">Steg {step} av {total}</span>
              <span className="text-sm text-text-muted">{Math.round((step / total) * 100)}% fullført</span>
            </div>

            <div className="relative">
              <div className="h-2 bg-gray-200 rounded-full">
                <div
                  className="h-2 bg-primary rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${((step - 1) / (total - 1)) * 100}%` }}
                />
              </div>

              {/* Step dots */}
              <div className="absolute -top-1 left-0 w-full flex justify-between">
                {Array.from({ length: total }, (_, i) => (
                  <div
                    key={i}
                    className={`w-4 h-4 rounded-full border-2 transition-all duration-300 ${
                      i + 1 <= step
                        ? 'bg-primary border-primary'
                        : 'bg-white border-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Step content */}
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-border-light">
            {children}
          </div>

          {/* Navigation buttons */}
          <div className="flex justify-end items-center mt-8">
            <button
              className="bg-primary hover:bg-primary-600 text-white font-semibold py-3 px-8 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-lg active:transform active:scale-95 flex items-center min-w-[140px] justify-center"
              disabled={nextDisabled || loading}
              onClick={handleNext}
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Lagrer...
                </>
              ) : (
                <>
                  {step === total ? 'Fullfør' : 'Neste'}
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </>
              )}
            </button>
          </div>

          {/* Skip option */}
          <div className="text-center mt-6">
            <button
              className="text-text-muted hover:text-text-secondary text-sm transition-colors duration-200"
              onClick={handleSkip}
            >
              Hopp over – gjør senere
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
